import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PhoneCall } from "lucide-react";

const LINES = ["Voice agents that", "answer, remind, and", "follow up."];

const Waveform = () => {
  const bars = Array.from({ length: 48 });
  return (
    <div className="flex items-center justify-center gap-[3px] h-16" aria-hidden="true">
      {bars.map((_, i) => {
        const center = Math.abs(i - 24) / 24;
        const h = 8 + (1 - center) * 44;
        return (
          <span
            key={i}
            className="wave-bar rounded-full bg-[#2563EB]"
            style={{
              width: 3,
              height: h,
              opacity: 0.25 + (1 - center) * 0.75,
              animationDelay: `${(i % 12) * 0.13}s`,
              animationDuration: `${1.4 + (i % 5) * 0.22}s`,
            }}
          />
        );
      })}
    </div>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden pt-24 pb-10">
      {/* abstract waveform backdrop with parallax */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1617994452722-4145e196248b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt=""
          className="w-full h-full object-cover opacity-[0.06] saturate-0"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF9F6]/40 via-transparent to-[#FAF9F6]" />
        {/* architectural grid lines */}
        <div className="absolute inset-0 max-w-7xl mx-auto px-5 sm:px-8 hidden md:grid grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="border-l border-zinc-200/60 last:border-r" />
          ))}
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.div style={{ y: yText }}>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7 }}
            className="font-mono2 text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#2563EB] mb-6 flex items-center gap-3"
          >
            <span className="inline-block w-8 h-px bg-[#2563EB]" />
            Voicxent · Voice-agent AI platform
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold tracking-tight leading-[1.02] text-[13vw] sm:text-7xl lg:text-[92px] text-[#09090B]"
          >
            {LINES.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.45 + i * 0.14, duration: 1.05, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 2 ? (
                    <>
                      follow up<span className="text-[#2563EB]">.</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="lg:col-span-6 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl"
            >
              For any business that talks to its customers. A Voicxent agent answers the phone,
              books appointments, reminds people, follows up — and records everything. It does the
              calling at scale; your people work the queue that needs a human.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="lg:col-span-6 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-enquire-button"
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                className="group inline-flex items-center gap-2.5 bg-[#09090B] text-white font-medium pl-6 pr-5 py-3.5 rounded-full hover:bg-[#2563EB] transition-colors duration-300"
              >
                Book a demo
                <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-use-cases-button"
                onClick={() => document.querySelector("#use-cases")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2.5 border border-zinc-300 text-zinc-900 font-medium px-6 py-3.5 rounded-full hover:border-[#09090B] hover:bg-white transition-all duration-300"
              >
                <PhoneCall size={16} className="text-[#2563EB]" />
                See the use cases
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="mt-12 sm:mt-16 border-t border-zinc-200 pt-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-1">
                <Waveform />
              </div>
              <div className="md:col-span-2 grid grid-cols-3 gap-4">
                {[
                  { k: "Hindi · English · Bengali", v: "Languages spoken" },
                  { k: "Inbound + Outbound", v: "One platform" },
                  { k: "Every call, on file", v: "Transcript + recording" },
                ].map((m) => (
                  <div key={m.k}>
                    <p className="font-display font-bold text-sm sm:text-base text-zinc-900 leading-snug">{m.k}</p>
                    <p className="font-mono2 text-[10px] sm:text-xs uppercase tracking-widest text-zinc-400 mt-1">{m.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
